import { useEffect, useState } from "react";
import { api, CATEGORY_LABELS, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { k: "suppliers", label: "Pemasok" },
  { k: "materials", label: "Bahan" },
  { k: "units", label: "Satuan" },
  { k: "locations", label: "Lokasi" },
];

export default function MasterData() {
  const { user } = useAuth();
  const [tab, setTab] = useState("suppliers");
  const [data, setData] = useState({ suppliers: [], materials: [], units: [], locations: [] });
  const [dialog, setDialog] = useState(null);
  const isAdmin = user?.role === "admin";

  async function loadAll() {
    const [s, m, u, l] = await Promise.all([api.get("/suppliers"), api.get("/materials"), api.get("/units"), api.get("/locations")]);
    setData({ suppliers: s.data, materials: m.data, units: u.data, locations: l.data });
  }
  useEffect(() => { loadAll(); }, []);

  async function del(coll, id) {
    try { await api.delete(`/${coll}/${id}`); toast.success("Data dihapus."); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-5">
      <div><h1 className="font-heading text-2xl font-extrabold">Master Data</h1>
        <p className="text-sm text-muted-foreground">Kelola pemasok, bahan, satuan, dan lokasi. Kode pemasok &amp; bahan dibuat unik otomatis.</p></div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`master-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`text-sm px-3 py-1.5 rounded-full border font-medium ${tab === t.k ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"}`}>{t.label}</button>
        ))}
      </div>

      <Card className="p-4">
        {isAdmin && <div className="mb-3"><Button size="sm" data-testid="master-add" onClick={() => setDialog({ tab })}><Plus className="w-4 h-4 mr-1" /> Tambah</Button></div>}
        <div className="overflow-x-auto">
          {tab === "suppliers" && <Table cols={["Kode","Nama","Alamat","Kontak"]} rows={data.suppliers.map((s) => ({ id: s.id, cells: [s.code, s.name, s.address, s.contact] }))} onDel={isAdmin ? (id) => del("suppliers", id) : null} />}
          {tab === "materials" && <Table cols={["Kode","Nama","Kategori","Satuan","Min Stok"]} rows={data.materials.map((m) => ({ id: m.id, cells: [m.code, m.name, CATEGORY_LABELS[m.category], m.default_unit, m.min_stock] }))} onDel={isAdmin ? (id) => del("materials", id) : null} />}
          {tab === "units" && <Table cols={["Satuan"]} rows={data.units.map((u) => ({ id: u.id, cells: [u.name] }))} onDel={isAdmin ? (id) => del("units", id) : null} />}
          {tab === "locations" && <Table cols={["Lokasi"]} rows={data.locations.map((l) => ({ id: l.id, cells: [l.name] }))} onDel={isAdmin ? (id) => del("locations", id) : null} />}
        </div>
      </Card>

      <AddDialog dialog={dialog} onClose={() => setDialog(null)} onDone={loadAll} />
    </div>
  );
}

function Table({ cols, rows, onDel }) {
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-left text-muted-foreground border-b">{cols.map((c) => <th key={c} className="py-2 font-medium">{c}</th>)}{onDel && <th></th>}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800">
            {r.cells.map((c, i) => <td key={i} className="py-2">{i === 0 ? <span className="batch-chip">{c}</span> : c}</td>)}
            {onDel && <td className="text-right"><Button size="sm" variant="ghost" data-testid={`del-${r.id}`} onClick={() => onDel(r.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="py-6 text-center text-muted-foreground">Belum ada data.</td></tr>}
      </tbody>
    </table>
  );
}

function AddDialog({ dialog, onClose, onDone }) {
  const [f, setF] = useState({});
  useEffect(() => { setF({ category: "bahan_baku_olahan_terkemas", default_unit: "pcs", min_stock: 0 }); }, [dialog]);
  if (!dialog) return null;
  const { tab } = dialog;

  async function save() {
    try {
      if (tab === "suppliers") await api.post("/suppliers", { name: f.name, address: f.address || "", contact: f.contact || "" });
      if (tab === "materials") await api.post("/materials", { name: f.name, category: f.category, default_unit: f.default_unit, min_stock: parseFloat(f.min_stock) || 0, requires_expiry: f.category !== "bahan_kemasan" });
      if (tab === "units") await api.post("/units", { name: f.name });
      if (tab === "locations") await api.post("/locations", { name: f.name });
      toast.success("Data tersimpan."); onClose(); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  return (
    <Dialog open={!!dialog} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Tambah {TABS.find((t) => t.k === tab)?.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Nama *</Label><Input data-testid="master-name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" /></div>
          {tab === "suppliers" && <>
            <div><Label className="text-xs">Alamat</Label><Input value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Kontak</Label><Input value={f.contact || ""} onChange={(e) => setF({ ...f, contact: e.target.value })} className="mt-1" /></div>
          </>}
          {tab === "materials" && <>
            <div><Label className="text-xs">Kategori</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger className="mt-1" data-testid="master-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Satuan Default</Label><Input value={f.default_unit} onChange={(e) => setF({ ...f, default_unit: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Min Stok</Label><Input type="number" value={f.min_stock} onChange={(e) => setF({ ...f, min_stock: e.target.value })} className="mt-1" /></div>
            </div>
          </>}
        </div>
        <DialogFooter><Button data-testid="master-save" onClick={save}>Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
